import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { GetCompanies200, GetCompaniesQueryParams } from '@eureca/recruiter-api-sdk/types';
import type { CompaniesSearch } from './companies-search.schema';

export type CompanyListEntry = GetCompanies200['items'][number];
export type CompanyListResponse = GetCompanies200;

/**
 * Error surfaced when the BFF refuses the request (auth/role/upstream failures).
 * `status` lets the UI branch — `403` renders the no-access fallback; everything
 * else falls into the generic retry block.
 */
export class CompaniesFetchError extends Error {
  readonly name = 'CompaniesFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/**
 * Build the query params the BFF expects. Drops `search` when below 2 chars
 * (the BFF would reject anyway; this saves the round-trip) and undefined fields.
 */
function paramsFromSearch(search: CompaniesSearch, pageOverride?: number): GetCompaniesQueryParams {
  const effectiveSearch = search.search && search.search.length >= 2 ? search.search : undefined;
  return {
    ...(effectiveSearch ? { search: effectiveSearch } : {}),
    ...(search.sort ? { sort: search.sort } : {}),
    dir: search.dir,
    page: pageOverride ?? search.page,
    pageSize: search.pageSize,
  };
}

async function fetchCompanies(
  params: GetCompaniesQueryParams,
  signal?: AbortSignal
): Promise<CompanyListResponse> {
  const res = await fetchClient<CompanyListResponse>({
    method: 'GET',
    url: '/companies',
    params,
    signal,
  });
  if (res.error) {
    throw new CompaniesFetchError(
      `Failed to load companies (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

/**
 * QueryKey for single-page (table view) queries. Includes `page` so changing
 * pages invalidates the cache properly.
 */
export function companiesQueryKey(search: CompaniesSearch) {
  return ['recruiter', 'companies', 'page', paramsFromSearch(search)] as const;
}

/**
 * QueryKey for infinite (cards view) queries. Excludes `page` because pages
 * are managed internally by `useInfiniteQuery` via `pageParam` — including
 * `page` here would invalidate the cache on every scroll-load.
 */
export function companiesInfiniteQueryKey(search: CompaniesSearch) {
  const { page: _page, ...rest } = paramsFromSearch(search, 1);
  return ['recruiter', 'companies', 'infinite', rest] as const;
}

/** Single-page query — used by the table view (classic pagination buttons). */
export function companiesQueryOptions(search: CompaniesSearch) {
  const params = paramsFromSearch(search);
  return queryOptions({
    queryKey: companiesQueryKey(search),
    queryFn: ({ signal }) => fetchCompanies(params, signal),
    staleTime: 60_000,
    // Avoid skeleton flicker when paginating — keep prior page visible until next arrives.
    placeholderData: keepPreviousData,
    retry: (failureCount, err) => {
      if (err instanceof CompaniesFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Infinite query — used by the cards view (scroll infinito).
 *
 * `getNextPageParam`: peek at `total` and current page; return the next page
 * number when more results remain, `undefined` otherwise (signals "no more").
 *
 * QueryKey deliberately omits `page` so each scroll-load extends the SAME
 * infinite cache entry. When sort/dir/search/pageSize change, the queryKey
 * changes → previous infinite query is canceled (AbortSignal) and a fresh
 * one starts at page 1 (FR-033 + edge case "reordenação durante scroll-loading").
 */
export function companiesInfiniteQueryOptions(search: CompaniesSearch) {
  return infiniteQueryOptions({
    queryKey: companiesInfiniteQueryKey(search),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      fetchCompanies(paramsFromSearch(search, pageParam as number), signal),
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.pageSize;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    staleTime: 60_000,
    // Keep the previous cards visible while the new query (different sort/search)
    // refetches, so the inline RefetchBanner can render over the prior data
    // instead of suspending into the full skeleton.
    placeholderData: keepPreviousData,
    retry: (failureCount, err) => {
      if (err instanceof CompaniesFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
